import React, { useEffect, useState, useCallback } from 'react';
import {
	ActionList,
	Button,
	Card,
	EmptyState,
	Heading,
	Popover,
	SkeletonBodyText,
	Stack,
	TextStyle,
} from '@shopify/polaris';
import { useMountedState, useSessionStorage } from 'react-use';
import {
	LineChart,
	Line,
	CartesianGrid,
	XAxis,
	YAxis,
	ResponsiveContainer,
	Tooltip,
} from 'recharts';
import { DateStyle, useI18n } from '@shopify/react-i18n';
import useAbortController from '../../../Utils/useAbortController';

const PageViewSection = ({ shared }) => {
	const { fetchJson, user, redirectOpt } = shared;
	const [i18n] = useI18n();
	const abortController = useAbortController();
	const isMounted = useMountedState();

	const [data, setData] = useState([]);
	const [range, setRange] = useSessionStorage('home.pageview.range', 'week');
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setIsLoading(true);

		fetchJson(
			`/Dashboard/GetPageViews?range=${encodeURIComponent(range)}`,
			'GET',
			undefined,
			abortController.signal,
			(data) => {
				if (!isMounted()) return;
				setData(data);
			},
			() => {
				if (!isMounted()) return;
				setIsLoading(false);
			}
		);

		return () => abortController.abort();
	}, [range]);

	const [popoverActive, setPopoverActive] = useState(false);

	const togglePopoverActive = useCallback(
		() => setPopoverActive((popoverActive) => !popoverActive),
		[]
	);

	return (
		<Card
			title={
				<Stack alignment='center'>
					<Stack.Item fill>
						<Heading element='h3'>
							{i18n.translate('SitemapPageViews').toUpperCase()}
						</Heading>
					</Stack.Item>
					<Stack.Item>
						<Popover
							active={popoverActive}
							activator={
								<Button
									disclosure
									onClick={togglePopoverActive}
								>
									{range == 'week'
										? i18n.translate('LastWeek')
										: range == 'month'
										? i18n.translate('LastMonth')
										: i18n.translate('LastYear')}
								</Button>
							}
							onClose={togglePopoverActive}
						>
							<ActionList
								onActionAnyItem={togglePopoverActive}
								items={[
									{
										id: 'week',
										content: i18n.translate('LastWeek'),
										onAction: () => setRange('week'),
									},
									{
										id: 'month',
										content: i18n.translate('LastMonth'),
										onAction: () => setRange('month'),
									},
									{
										id: 'year',
										content: i18n.translate('LastYear'),
										onAction: () => setRange('year'),
									},
								]}
							/>
						</Popover>
					</Stack.Item>
				</Stack>
			}
		>
			<Card.Section>
				{isLoading ? (
					<SkeletonBodyText lines={12} />
				) : data.length == 0 ? (
					<EmptyState
						heading={i18n.translate('NoData')}
						image='../img/users_per_minute.svg'
						action={{
							content: `${i18n.translate(
								'Common.Action.View'
							)} ${i18n.translate('Common.Sitemap')}`,
							onAction: () =>
								redirectOpt(
									`${user.url}/${user.pathPrefix}/sitemap`,
									true
								),
						}}
					>
						<p>{i18n.translate('NoDataMsg')}</p>
					</EmptyState>
				) : (
					<ResponsiveContainer aspect={2.5}>
						<LineChart data={data}>
							<Line
								type='monotone'
								dataKey='views'
								stroke='#8884d8'
							/>
							<CartesianGrid strokeDasharray='3 3' />
							<XAxis
								dataKey='date'
								type='number'
								domain={['auto', 'auto']}
								tickFormatter={(unixTime) =>
									i18n.formatDate(new Date(unixTime), {
										style: DateStyle.Short,
									})
								}
							/>
							<YAxis
								allowDecimals={false}
								tickFormatter={(views) =>
									i18n.formatNumber(views)
								}
							/>
							<Tooltip
								formatter={(value, name, props) => [
									i18n.formatNumber(value),
									i18n.translate('NumberOfViews'),
								]}
								labelFormatter={(unixTime) => (
									<TextStyle variation='strong'>
										{i18n.formatDate(new Date(unixTime), {
											style: DateStyle.Short,
										})}
									</TextStyle>
								)}
							/>
						</LineChart>
					</ResponsiveContainer>
				)}
			</Card.Section>
		</Card>
	);
};

export default PageViewSection;
